import { refreshRequest } from '../../../views/login/actions';

export interface RootState {
  auth?: {
    jwtToken?: string;
    refreshToken?: string;
    selectedSite?: number | null;
    session?: {
      role?: string;
    };
  };
  siteSettings?: {
    site?: {
      groupId?: any;
    };
  };
  [key: string]: any;
}

function serializeParams(params: unknown): string {
  if (!params || typeof params !== 'object') return '';
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params as Record<string, unknown>)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      value.forEach((v) => {
        if (v !== undefined && v !== null) search.append(key, String(v));
      });
      continue;
    }
    search.append(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

async function performRequest(args: any, url: string, token?: string): Promise<Response> {
  const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8081';
  const queryString = serializeParams(args.params);
  const fullUrl = `${baseUrl}/${url}${queryString}`;

  // File uploads send FormData; the browser has to set its own multipart boundary,
  // so leave Content-Type off and pass the body through untouched.
  const isFormData = args.body instanceof FormData;
  const headers: HeadersInit = {
    Accept: 'application/json',
  };
  if (!isFormData) headers['Content-Type'] = 'application/json';
  if (token) headers['X-Auth-Token'] = token;

  return fetch(fullUrl, {
    method: args.method || 'GET',
    headers,
    body: isFormData ? args.body : args.body ? JSON.stringify(args.body) : undefined,
    credentials: 'include',
    mode: 'cors',
    cache: 'no-cache',
  });
}

const REFRESH_POLL_MS = 50;
const REFRESH_TIMEOUT_MS = 10000;

// One refresh at a time, shared by every query that 401s together. The saga's
// takeLeading already collapses the network call; this collapses the waiting so
// a screen full of queries produces a single refresh and a single replay each.
let inFlightRefresh: Promise<string | null> | null = null;

/**
 * Asks the auth saga to refresh, then resolves with the new token.
 *
 * RTK Query lives outside the saga middleware, so it cannot `take(REFRESH_SUCCESS)`.
 * Watching the store for the token to change is the equivalent: the auth
 * reducer writes the new jwtToken on REFRESH_SUCCESS, the same action the saga
 * path waits for.
 *
 * It also covers failure. A failed refresh dispatches UNAUTHORIZED, which
 * requestErrorSaga turns into LOGOUT_REQUEST -> logout -> LOGOUT_SUCCESS, and
 * the reducer resets jwtToken to ''. We resolve null there rather than hanging,
 * which is what the saga path does today: its `take(REFRESH_SUCCESS)` never
 * settles when the refresh fails.
 */
function refreshToken(api: any, expiredToken?: string): Promise<string | null> {
  if (inFlightRefresh) return inFlightRefresh;

  inFlightRefresh = new Promise<string | null>((resolve) => {
    const startedAt = Date.now();

    const auth = (api.getState() as RootState).auth ?? {};
    api.dispatch(
      refreshRequest({
        jwtToken: expiredToken ?? auth.jwtToken,
        refreshToken: auth.refreshToken,
      })
    );

    const poll = () => {
      const token = (api.getState() as RootState).auth?.jwtToken;
      if (!token) return resolve(null); // refresh failed and we were logged out
      if (token !== expiredToken) return resolve(token);
      if (Date.now() - startedAt > REFRESH_TIMEOUT_MS) return resolve(null);
      setTimeout(poll, REFRESH_POLL_MS);
    };

    poll();
  }).finally(() => {
    inFlightRefresh = null;
  });

  return inFlightRefresh;
}

export const baseQueryWithRefresh = async (args: any, api: any): Promise<any> => {
  const state = api.getState() as RootState;
  const siteId = state.auth?.selectedSite;
  const isGlobal = args.global === true;

  if (!state.auth?.jwtToken) {
    return { error: { status: 'UNAUTHENTICATED', data: 'No authentication token found' } };
  }

  if (!isGlobal && state.auth?.session?.role === 'Admin' && !state.siteSettings?.site?.groupId) {
    return {
      error: { status: 'SITE_SETTINGS_NOT_LOADED', data: 'Site settings not loaded yet' },
    };
  }

  // URLs starting with `manager/` skip the site prefix so callers can hit non
  // site-scoped routes without double-prefixing. `global: true` does the same for
  // endpoints that are not site-scoped at all (integrations/daily-numbers/...).
  let url = args.url;
  if (!isGlobal && siteId && !url.startsWith('manager/')) {
    url = `manager/sites/${siteId}/${url}`;
  }

  const token = state.auth?.jwtToken;

  try {
    let response = await performRequest(args, url, token);

    // An expired JWT is recoverable: refresh through the same saga the
    // rest of the app uses, then replay the call once.
    if (response.status === 401) {
      const freshToken = await refreshToken(api, token);
      if (!freshToken) {
        return { error: { status: 401, data: 'Session expired. Please sign in again.' } };
      }
      response = await performRequest(args, url, freshToken);
    }

    if (!response.ok) {
      const errorText = await response.text();
      return { error: { status: response.status, data: errorText } };
    }

    const json = await response.json();
    const data = Array.isArray(json) ? { results: json } : json;
    return { data };
  } catch (fetchError: unknown) {
    const errorMessage = fetchError instanceof Error ? fetchError.message : 'Unknown fetch error';
    return { error: { status: 'FETCH_ERROR', data: errorMessage } };
  }
};
